// Lazy route values for routes.js files.
//
// A routes.js normally maps paths to statically imported components, which
// bundles every route's code into the main chunk — including admin panels
// that most visitors never open. lazyRoute defers a route's component to a
// dynamic import, so the bundler splits it into its own chunk loaded on
// first navigation:
//
//   import { lazyRoute } from "aldoo-ra/Router/lazy-route"
//   export default {
//     "/admin/cms*": lazyRoute(() => import("aldoo-ra/CMS"), adminRole),
//   }
//
// Plain component values keep working unchanged — the marker is explicit
// because a loader function is indistinguishable from a function component.
//
// The boundary renders null during SSR: lazy routes are meant for
// auth-gated / client-only surfaces (admin) where SSR carries no value.
// Routes that need SSR or an onBeforeRender hook must stay static.
import React, { Suspense, useEffect, useState } from "react"

export const isLazyRoute = (value) =>
  Boolean(value) && typeof value === "object" && value.__lazyRoute === true

/**
 * @param {() => Promise<{default: React.ComponentType}>} load - dynamic import of the route module
 * @param {(Component: React.ComponentType) => React.ComponentType} [wrap] - optional HOC applied after load (e.g. adminRole)
 */
export const lazyRoute = (load, wrap) => ({ __lazyRoute: true, load, wrap })

const fallback = (
  <div className="w-full flex items-center justify-center py-24">
    <div className="w-8 h-8 rounded-full border-2 border-current border-t-transparent animate-spin opacity-40" />
  </div>
)

// One boundary (and one React.lazy instance) per marker — resolve() may run
// on every render and React.lazy must stay referentially stable.
const boundaryCache = new WeakMap()

export const getLazyBoundary = (marker) => {
  let Boundary = boundaryCache.get(marker)
  if (Boundary) return Boundary

  const Lazy = React.lazy(() =>
    marker.load().then((module) => ({
      default: marker.wrap ? marker.wrap(module.default) : module.default,
    }))
  )

  Boundary = (props) => {
    // Client-only: lazy routes skip SSR by design (see header comment).
    //
    // Hydration compares the client's FIRST render against the server's
    // html, so that first render has to be the same nothing the server
    // produced — a Suspense boundary in its place is a structural mismatch,
    // and React throws it away and re-renders the whole root from scratch
    // (#418 → #423, and then #300 from anything that had subscribed in the
    // meantime). The route appears one tick later instead.
    const [mounted, setMounted] = useState(false)
    useEffect(() => setMounted(true), [])
    if (!mounted) return null
    return (
      <Suspense fallback={fallback}>
        <Lazy {...props} />
      </Suspense>
    )
  }
  boundaryCache.set(marker, Boundary)
  return Boundary
}
